/**
 * Emissions Tracker Routes
 *
 * Carbon emission tracking and reporting following GHG Protocol standards.
 * Uses initiative-scoped routes similar to company-tracker pattern.
 *
 * Navigation Structure:
 * - Overview: Dashboard with summary
 * - Emissions: Scope 1/2/3 detail pages (sub-tabs)
 * - Insights: Analytics with trends, breakdown, facilities, intensity
 * - Activities: Activity data management
 * - Reports: Reporting and export
 * - Admin: Settings and emission factors
 */

import { RoutesInterface } from '@g17eco/types/routes';

export const rootAppPath = 'emissions';

export const getRootAppPath = () => rootAppPath;

const routeDefaults = {
  auth: true,
  getRootAppPath,
  requiresInitiativeId: true,
  appPermissionId: 'app_emissions_tracker',
};

export const emissionsTrackerRoutes: RoutesInterface = {
  EMISSIONS_TRACKER: {
    id: 'emissions_tracker_root',
    label: 'Emissions Tracker',
    tooltip: 'Carbon Emissions Tracker',
    path: `/${rootAppPath}`,
    icon: 'fa-leaf',
    appPermissionId: 'app_emissions_tracker',
    exact: false,
    auth: true,
    getRootAppPath,
  },
  // Initiative Structure Navigation
  EMISSIONS_NAVIGATE_BY_MAP: {
    ...routeDefaults,
    id: 'emissions_navigation_map',
    label: 'Organisational structure',
    path: `/${rootAppPath}/map/:initiativeId`,
    icon: 'fa-sitemap',
    tooltip: 'Navigation Tree of the Reporting Level Dashboards',
  },
  EMISSIONS_NAVIGATE_BY_ARCHIVED: {
    ...routeDefaults,
    id: 'emissions_archived_map',
    label: 'Archived initiatives',
    path: `/${rootAppPath}/map/:initiativeId/archived`,
    icon: 'fa-sitemap',
    tooltip: 'Navigation Tree of the Archived Reporting Level Dashboards',
  },
  // Main Navigation: Overview
  EMISSIONS_OVERVIEW: {
    ...routeDefaults,
    id: 'emissions_overview',
    label: 'Overview',
    path: `/${rootAppPath}/overview/:initiativeId`,
    exact: true,
    tooltip: 'Emissions Overview Dashboard',
  },
  // Main Navigation: Emissions (Scope data pages)
  EMISSIONS_SCOPES: {
    ...routeDefaults,
    id: 'emissions_scopes',
    label: 'Emissions',
    path: `/${rootAppPath}/scope/:initiativeId/:scope?`,
    exact: true,
    tooltip: 'Emissions by Scope',
  },
  // Emissions Scope Sub-tabs
  EMISSIONS_SCOPE1: {
    ...routeDefaults,
    id: 'emissions_scope1',
    label: 'Scope 1',
    path: `/${rootAppPath}/scope/:initiativeId/scope1`,
    exact: true,
    tooltip: 'Scope 1 Direct Emissions',
  },
  EMISSIONS_SCOPE2: {
    ...routeDefaults,
    id: 'emissions_scope2',
    label: 'Scope 2',
    path: `/${rootAppPath}/scope/:initiativeId/scope2`,
    exact: true,
    tooltip: 'Scope 2 Indirect Emissions',
  },
  EMISSIONS_SCOPE3: {
    ...routeDefaults,
    id: 'emissions_scope3',
    label: 'Scope 3',
    path: `/${rootAppPath}/scope/:initiativeId/scope3`,
    exact: true,
    tooltip: 'Scope 3 Value Chain Emissions',
  },
  // Main Navigation: Insights (Analytics)
  EMISSIONS_INSIGHTS: {
    ...routeDefaults,
    id: 'emissions_insights',
    label: 'Insights',
    path: `/${rootAppPath}/insights/:initiativeId`,
    exact: true,
    tooltip: 'Analytics & Insights',
  },
  // Main Navigation: Activities
  EMISSIONS_ACTIVITIES: {
    ...routeDefaults,
    id: 'emissions_activities',
    label: 'Activities',
    path: `/${rootAppPath}/activities/:initiativeId`,
    exact: true,
    tooltip: 'Emission Activities',
  },
  EMISSIONS_ACTIVITY_NEW: {
    ...routeDefaults,
    id: 'emissions_activity_new',
    label: 'New Activity',
    path: `/${rootAppPath}/activities/:initiativeId/new`,
    exact: true,
  },
  EMISSIONS_ACTIVITY_EDIT: {
    ...routeDefaults,
    id: 'emissions_activity_edit',
    label: 'Edit Activity',
    path: `/${rootAppPath}/activities/:initiativeId/:activityId/edit`,
    exact: true,
  },
  EMISSIONS_ACTIVITY_DETAIL: {
    ...routeDefaults,
    id: 'emissions_activity_detail',
    label: 'Activity Detail',
    path: `/${rootAppPath}/activities/:initiativeId/:activityId`,
    exact: true,
  },
  // Main Navigation: Assets
  EMISSIONS_ASSETS: {
    ...routeDefaults,
    id: 'emissions_assets',
    label: 'Assets',
    path: `/${rootAppPath}/assets/:initiativeId`,
    exact: true,
    tooltip: 'Asset Emissions Overview',
  },
  EMISSIONS_ASSET_DASHBOARD: {
    ...routeDefaults,
    id: 'emissions_asset_dashboard',
    label: 'Asset',
    path: `/${rootAppPath}/assets/:initiativeId/:assetId`,
    exact: true,
    tooltip: 'Asset emissions, coverage, and activities',
  },
  // Main Navigation: Download Reports
  EMISSIONS_REPORTS: {
    ...routeDefaults,
    id: 'emissions_downloads',
    label: 'Downloads',
    path: `/${rootAppPath}/downloads/:initiativeId/:reportType?`,
    exact: false,
    tooltip: 'Download Emission Reports and Data',
  },
  // Legacy route - redirect to overview
  EMISSIONS_DASHBOARD: {
    ...routeDefaults,
    id: 'emissions_dashboard',
    label: 'Dashboard',
    path: `/${rootAppPath}/dashboard/:initiativeId`,
    exact: true,
    tooltip: 'Emissions Dashboard',
  },
  // Admin Navigation
  EMISSIONS_ADMIN: {
    ...routeDefaults,
    id: 'emissions_admin',
    label: 'Admin',
    path: `/${rootAppPath}/admin/:initiativeId`,
    exact: true,
    tooltip: 'Emissions Admin Settings',
  },
  EMISSIONS_FACTORS: {
    ...routeDefaults,
    id: 'emissions_factors',
    label: 'Emission Factors',
    path: `/${rootAppPath}/admin/:initiativeId/factors`,
    exact: true,
    tooltip: 'Manage Emission Factors',
  },
  EMISSIONS_SETTINGS: {
    ...routeDefaults,
    id: 'emissions_settings',
    label: 'Settings',
    path: `/${rootAppPath}/admin/:initiativeId/settings`,
    exact: true,
    tooltip: 'Configure Calculation and Reporting Settings',
  },
  EMISSIONS_BULK_IMPORT: {
    ...routeDefaults,
    id: 'emissions_bulk_import',
    label: 'Bulk Import',
    path: `/${rootAppPath}/admin/:initiativeId/import`,
    exact: true,
    tooltip: 'Import Activity Data',
  },
  EMISSIONS_DATA_EXPORT: {
    ...routeDefaults,
    id: 'emissions_data_export',
    label: 'Data Export',
    path: `/${rootAppPath}/admin/:initiativeId/export`,
    exact: true,
    tooltip: 'Export Emissions Data',
  },
  EMISSIONS_MANAGE_USERS: {
    ...routeDefaults,
    id: 'emissions_manage_users',
    label: 'Manage Users',
    path: `/${rootAppPath}/admin/:initiativeId/users/:page?`,
    exact: false,
    tooltip: 'Manage Users',
  },
  EMISSIONS_BUSINESS_METRICS: {
    ...routeDefaults,
    id: 'emissions_business_metrics',
    label: 'Business Metrics',
    path: `/${rootAppPath}/admin/:initiativeId/business-metrics`,
    exact: true,
    tooltip: 'Configure Business Metrics for Intensity Calculations',
  },
  EMISSIONS_IMPORT_HISTORY: {
    ...routeDefaults,
    id: 'emissions_import_history',
    label: 'Import History',
    path: `/${rootAppPath}/admin/:initiativeId/import-history`,
    exact: true,
    tooltip: 'View Past Import Records',
  },
  EMISSIONS_EVIDENCE_LIBRARY: {
    ...routeDefaults,
    id: 'emissions_evidence_library',
    label: 'Evidence Library',
    path: `/${rootAppPath}/admin/:initiativeId/evidence`,
    exact: true,
    tooltip: 'Browse and manage all uploaded evidence documents',
  },
};
